<template>
    <v-dialog v-model="dialog" max-width="780" persistent scrollable>
        <v-card rounded="lg">

            <!-- Header -->
            <v-card-title class="dialog-header pa-4 pb-2 d-flex align-center">
                <v-icon size="18" class="me-2" color="primary">mdi-domain</v-icon>
                <span class="text-body-1 font-weight-semibold text-truncate">
                    Abbonamento — {{ nomeAzienda }}
                </span>
                <v-spacer />
                <v-btn icon size="small" variant="text" @click="chiudi">
                    <v-icon size="18">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="pa-0" style="max-height: 78vh;">

                <template v-if="loading">
                    <div class="pa-6"><v-skeleton-loader type="article" /></div>
                </template>

                <template v-else>
                    <v-tabs v-model="tab" color="primary" class="px-4 pt-2">
                        <v-tab value="abbonamento">Abbonamento</v-tab>
                        <v-tab value="richieste">
                            Richieste
                            <v-badge v-if="richiestePendenti > 0" :content="richiestePendenti"
                                color="error" inline class="ms-1" />
                        </v-tab>
                        <v-tab value="storico">Storico richieste</v-tab>
                    </v-tabs>

                    <v-divider />

                    <v-window v-model="tab">

                        <!-- ══ TAB: Abbonamento (modifica diretta) ══ -->
                        <v-window-item value="abbonamento" class="pa-4">

                            <!-- Stato attuale -->
                            <v-alert v-if="abbonamento" type="info" variant="tonal" density="compact" class="mb-4">
                                Stato attuale:
                                <strong>{{ chipStatoLabel(abbonamento.stato) }}</strong> —
                                Scadenza: <strong>{{ formatDate(abbonamento.dataScadenza) }}</strong>
                                <span v-if="abbonamento.giorniAllaScadenza !== undefined">
                                    ({{ abbonamento.giorniAllaScadenza }} giorni)
                                </span>
                            </v-alert>

                            <v-form ref="formAbbRef">
                                <v-row dense>
                                    <!-- Tipo licenza -->
                                    <v-col cols="12" sm="4">
                                        <v-select v-model="form.tipoLicenza"
                                            :items="[{ title: 'Basic (3 utenti – €180/anno)', value: 'basic' },
                                                     { title: 'Pro (10 utenti – €336/anno)', value: 'pro' }]"
                                            label="Piano licenza" variant="outlined" density="compact"
                                            @update:model-value="onCambioPiano" />
                                    </v-col>

                                    <!-- Utenti extra -->
                                    <v-col cols="12" sm="3">
                                        <v-text-field v-model.number="form.utentiExtra"
                                            label="Utenti extra" type="number" :min="0"
                                            variant="outlined" density="compact"
                                            hint="€ 48/utente/anno" persistent-hint
                                            @update:model-value="ricalcolaProrataAdmin" />
                                    </v-col>

                                    <!-- Stato -->
                                    <v-col cols="12" sm="5">
                                        <v-select v-model="form.stato"
                                            :items="[
                                                { title: 'In attesa di pagamento', value: 'attesa_pagamento' },
                                                { title: 'Attiva', value: 'attiva' },
                                                { title: 'Sospesa', value: 'sospesa' },
                                                { title: 'Scaduta', value: 'scaduta' }
                                            ]"
                                            label="Stato abbonamento" variant="outlined" density="compact" />
                                    </v-col>

                                    <!-- Date -->
                                    <v-col cols="12" sm="6">
                                        <v-text-field v-model="form.dataInizio" label="Data inizio"
                                            type="date" variant="outlined" density="compact" />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-text-field v-model="form.dataScadenza" label="Data scadenza"
                                            type="date" variant="outlined" density="compact"
                                            hint="Data grazia = +7 giorni automatici" persistent-hint />
                                    </v-col>

                                    <!-- Note operative -->
                                    <v-col cols="12">
                                        <v-textarea v-model="form.note" label="Note interne (non visibili al cliente)"
                                            variant="outlined" density="compact" :rows="2" autoGrow :max-rows="5" />
                                    </v-col>
                                </v-row>

                                <!-- Riepilogo prezzi e pro-rata -->
                                <v-card class="mt-2 elevation-0" color="grey-lighten-4" rounded="lg">
                                    <v-card-text class="pa-3">
                                        <div class="riepilogo-grid">
                                            <span class="text-body-2">Piano {{ form.tipoLicenza }}</span>
                                            <span class="text-body-2 font-weight-medium">
                                                € {{ prezzoBaseCalcolato.toFixed(2) }}/anno
                                            </span>
                                            <span class="text-body-2">
                                                {{ form.utentiExtra }} utenti extra
                                            </span>
                                            <span class="text-body-2 font-weight-medium">
                                                € {{ prezzoExtraCalcolato.toFixed(2) }}/anno
                                            </span>
                                            <span class="text-body-2 font-weight-bold">Totale annuo</span>
                                            <span class="text-body-2 font-weight-bold text-primary">
                                                € {{ prezzoTotaleCalcolato.toFixed(2) }}
                                            </span>
                                        </div>

                                        <!-- Pro-rata (solo se c'è abbonamento attivo e il piano cambia) -->
                                        <template v-if="prorata && abbonamento">
                                            <v-divider class="my-2" />
                                            <div class="text-body-2 font-weight-semibold mb-1">
                                                📊 Pro-rata stimato
                                            </div>
                                            <div class="riepilogo-grid text-caption">
                                                <span>Giorni rimanenti</span>
                                                <span>{{ prorata.giorniRimanenti }} / {{ prorata.giorniTotali }}</span>
                                                <span>Differenza da fatturare</span>
                                                <span :class="prorata.importoProrata >= 0 ? 'text-error' : 'text-success'">
                                                    {{ prorata.importoProrata >= 0 ? '+' : '' }}€ {{ Math.abs(prorata.importoProrata).toFixed(2) }}
                                                </span>
                                            </div>
                                        </template>
                                    </v-card-text>
                                </v-card>
                            </v-form>

                            <div class="d-flex justify-end mt-4 gap-2">
                                <v-btn variant="text" @click="chiudi">Annulla</v-btn>
                                <v-btn color="primary" variant="elevated" :loading="salvando"
                                    @click="salvaAbbonamento" prepend-icon="mdi-content-save">
                                    Salva modifiche
                                </v-btn>
                            </div>
                        </v-window-item>

                        <!-- ══ TAB: Richieste pendenti ══ -->
                        <v-window-item value="richieste" class="pa-4">

                            <div v-if="richiestePendentiLista.length === 0" class="text-center py-6 text-grey">
                                <v-icon size="36" class="mb-2">mdi-inbox-outline</v-icon>
                                <p class="text-body-2">Nessuna richiesta pendente</p>
                            </div>

                            <v-card v-for="r in richiestePendentiLista" :key="r.richiestaId"
                                class="mb-3 elevation-1" rounded="lg">
                                <v-card-text class="pa-3">
                                    <div class="d-flex align-center mb-2">
                                        <v-chip size="x-small" color="warning" variant="tonal" class="me-2">
                                            {{ labelTipoRichiesta(r.tipoRichiesta) }}
                                        </v-chip>
                                        <span class="text-caption text-medium-emphasis">
                                            {{ formatDate(r.dataRichiesta) }}
                                        </span>
                                    </div>

                                    <div class="richiesta-grid text-body-2 mb-2">
                                        <span class="text-medium-emphasis">Piano richiesto</span>
                                        <span>{{ r.pianoRichiesto || '—' }}</span>
                                        <span class="text-medium-emphasis">Utenti extra</span>
                                        <span>{{ r.utentiExtraRichiesti ?? '—' }}</span>
                                        <span class="text-medium-emphasis">Note cliente</span>
                                        <span>{{ r.noteCliente || '—' }}</span>
                                    </div>

                                    <!-- Pro-rata per upgrade/downgrade -->
                                    <template v-if="(r.tipoRichiesta === 'upgrade' || r.tipoRichiesta === 'downgrade') && prorataRichieste[r.richiestaId]">
                                        <v-card class="mb-2 elevation-0" color="blue-lighten-5" rounded="lg">
                                            <v-card-text class="pa-2 text-caption">
                                                <div class="richiesta-grid">
                                                    <span>Piano attuale annuo</span>
                                                    <span>€ {{ prorataRichieste[r.richiestaId].prezzoVecchio.toFixed(2) }}</span>
                                                    <span>Piano nuovo annuo</span>
                                                    <span>€ {{ prorataRichieste[r.richiestaId].prezzoNuovo.toFixed(2) }}</span>
                                                    <span>Giorni rimanenti</span>
                                                    <span>{{ prorataRichieste[r.richiestaId].giorniRimanenti }} gg</span>
                                                    <span class="font-weight-bold">Da fatturare</span>
                                                    <span class="font-weight-bold text-primary">
                                                        € {{ Math.abs(prorataRichieste[r.richiestaId].importoProrata).toFixed(2) }}
                                                    </span>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </template>

                                    <!-- Note operatore -->
                                    <v-textarea v-model="noteEvariazioni[r.richiestaId]"
                                        label="Note operatore (visibili al cliente)"
                                        variant="outlined" density="compact" :rows="2" class="mb-2" hide-details />

                                    <!-- Azioni -->
                                    <div class="d-flex gap-2 justify-end">
                                        <v-btn variant="tonal" color="error" size="small"
                                            :loading="evasionePendente[r.richiestaId] === 'rifiutata'"
                                            @click="evadiRichiesta(r, 'rifiutata')">
                                            Rifiuta
                                        </v-btn>
                                        <v-btn variant="elevated" color="success" size="small"
                                            :loading="evasionePendente[r.richiestaId] === 'approvata'"
                                            @click="evadiRichiesta(r, 'approvata')">
                                            Approva
                                        </v-btn>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-window-item>

                        <!-- ══ TAB: Storico ══ -->
                        <v-window-item value="storico" class="pa-4">
                            <v-table density="compact">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Tipo</th>
                                        <th>Piano</th>
                                        <th>Extra</th>
                                        <th>Stato</th>
                                        <th>Pro-rata</th>
                                        <th>Note operatore</th>
                                        <th>Evaso il</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="r in tutteRichieste" :key="r.richiestaId">
                                        <td>{{ formatDate(r.dataRichiesta) }}</td>
                                        <td>{{ labelTipoRichiesta(r.tipoRichiesta) }}</td>
                                        <td>{{ r.pianoRichiesto || '—' }}</td>
                                        <td>{{ r.utentiExtraRichiesti ?? '—' }}</td>
                                        <td>
                                            <v-chip size="x-small"
                                                :color="chipRichiestaColore(r.stato)" variant="tonal">
                                                {{ chipRichiestaLabel(r.stato) }}
                                            </v-chip>
                                        </td>
                                        <td>{{ r.importoProrata ? '€ ' + r.importoProrata.toFixed(2) : '—' }}</td>
                                        <td class="text-caption text-medium-emphasis">{{ r.noteOperatore || '—' }}</td>
                                        <td>{{ formatDate(r.dataEvasione) }}</td>
                                    </tr>
                                    <tr v-if="tutteRichieste.length === 0">
                                        <td colspan="8" class="text-center text-grey py-4">Nessuna richiesta</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-window-item>

                    </v-window>
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { ref, computed, reactive } from 'vue'
import {
    get_dettaglio_azienda,
    crea_abbonamento,
    modifica_abbonamento,
    calcola_prorata_admin,
    evadi_richiesta
} from '@/api/api.abbonamento.js'

const PREZZI = { basic: 180, pro: 336, extra: 48 }

export default {
    name: 'DialogAbbonamentoAdmin',
    emits: ['aggiornato'],

    setup(props, { emit }) {
        const dialog     = ref(false)
        const loading    = ref(false)
        const salvando   = ref(false)
        const tab        = ref('abbonamento')

        const aziendaId   = ref(null)
        const nomeAzienda = ref('')
        const abbonamento = ref(null)
        const tutteRichieste = ref([])
        const formAbbRef  = ref(null)
        const prorata     = ref(null)

        // note per ogni richiesta pendente (keyed by richiestaId)
        const noteEvariazioni  = reactive({})
        const evasionePendente = reactive({})
        const prorataRichieste = reactive({})

        const form = ref({
            tipoLicenza:  'basic',
            utentiExtra:  0,
            dataInizio:   today(),
            dataScadenza: addYear(today()),
            stato:        'attesa_pagamento',
            note:         ''
        })

        // ── Computed prezzi ───────────────────────────────────────────────────
        const prezzoBaseCalcolato  = computed(() => PREZZI[form.value.tipoLicenza] ?? 180)
        const prezzoExtraCalcolato = computed(() => (form.value.utentiExtra || 0) * PREZZI.extra)
        const prezzoTotaleCalcolato = computed(() => prezzoBaseCalcolato.value + prezzoExtraCalcolato.value)

        const richiestePendentiLista = computed(() =>
            tutteRichieste.value.filter(r => r.stato === 'in_attesa'))

        const richiestePendenti = computed(() => richiestePendentiLista.value.length)

        // ── Apri ─────────────────────────────────────────────────────────────
        const apri = async (az) => {
            aziendaId.value   = az.aziendaId
            nomeAzienda.value = az.ragioneSociale
            tab.value         = 'abbonamento'
            dialog.value      = true
            loading.value     = true

            try {
                const res = await get_dettaglio_azienda(az.aziendaId)
                const data = res.data
                abbonamento.value  = data.abbonamento
                tutteRichieste.value = data.richieste || []

                // Pre-popola form con dati esistenti
                if (data.abbonamento) {
                    const a = data.abbonamento
                    form.value = {
                        tipoLicenza:  a.tipoLicenza,
                        utentiExtra:  a.utentiExtra,
                        dataInizio:   toInputDate(a.dataInizio),
                        dataScadenza: toInputDate(a.dataScadenza),
                        stato:        a.stato,
                        note:         ''
                    }
                } else {
                    form.value = {
                        tipoLicenza:  'basic',
                        utentiExtra:  0,
                        dataInizio:   today(),
                        dataScadenza: addYear(today()),
                        stato:        'attesa_pagamento',
                        note:         ''
                    }
                }

                // Pre-carica pro-rata per le richieste upgrade/downgrade pendenti
                for (const r of richiestePendentiLista.value) {
                    noteEvariazioni[r.richiestaId] = ''
                    if ((r.tipoRichiesta === 'upgrade' || r.tipoRichiesta === 'downgrade') && abbonamento.value) {
                        try {
                            const pr = await calcola_prorata_admin(
                                az.aziendaId, r.pianoRichiesto, r.utentiExtraRichiesti ?? 0)
                            prorataRichieste[r.richiestaId] = pr.data
                        } catch { /* ignora */ }
                    }
                }
            } finally {
                loading.value = false
            }
        }

        const chiudi = () => { dialog.value = false }

        // ── Ricalcola pro-rata quando cambia piano/extra nel form modifica ────
        const onCambioPiano = () => ricalcolaProrataAdmin()

        const ricalcolaProrataAdmin = async () => {
            if (!abbonamento.value) { prorata.value = null; return }
            try {
                const res = await calcola_prorata_admin(
                    aziendaId.value, form.value.tipoLicenza, form.value.utentiExtra)
                prorata.value = res.data
            } catch {
                prorata.value = null
            }
        }

        // ── Salva abbonamento (crea o modifica) ───────────────────────────────
        const salvaAbbonamento = async () => {
            salvando.value = true
            try {
                const payload = { ...form.value }
                if (abbonamento.value) {
                    await modifica_abbonamento(aziendaId.value, payload)
                } else {
                    await crea_abbonamento(aziendaId.value, payload)
                }
                emit('aggiornato')
                chiudi()
            } finally {
                salvando.value = false
            }
        }

        // ── Evadi richiesta ───────────────────────────────────────────────────
        const evadiRichiesta = async (richiesta, stato) => {
            evasionePendente[richiesta.richiestaId] = stato
            try {
                await evadi_richiesta(richiesta.richiestaId, {
                    stato,
                    noteOperatore: noteEvariazioni[richiesta.richiestaId] || ''
                })
                // Aggiorna dati
                const res = await get_dettaglio_azienda(aziendaId.value)
                abbonamento.value    = res.data.abbonamento
                tutteRichieste.value = res.data.richieste || []

                if (abbonamento.value) {
                    form.value.tipoLicenza  = abbonamento.value.tipoLicenza
                    form.value.utentiExtra  = abbonamento.value.utentiExtra
                    form.value.stato        = abbonamento.value.stato
                    form.value.dataInizio   = toInputDate(abbonamento.value.dataInizio)
                    form.value.dataScadenza = toInputDate(abbonamento.value.dataScadenza)
                }

                emit('aggiornato')
            } finally {
                evasionePendente[richiesta.richiestaId] = null
            }
        }

        // ── Helpers display ───────────────────────────────────────────────────
        const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—'

        const chipStatoLabel = (s) => ({
            attiva: 'Attiva', attesa_pagamento: 'In attesa',
            sospesa: 'Sospesa', scaduta: 'Scaduta'
        }[s] || s)

        const labelTipoRichiesta = (t) => ({
            nuovo: 'Nuovo', upgrade: 'Upgrade', downgrade: 'Downgrade',
            extra_utenti: 'Utenti extra', rimozione_extra: 'Rimozione extra'
        }[t] || t)

        const chipRichiestaColore = (s) =>
            ({ in_attesa: 'warning', approvata: 'success', rifiutata: 'error' }[s] || 'grey')

        const chipRichiestaLabel = (s) =>
            ({ in_attesa: 'In attesa', approvata: 'Approvata', rifiutata: 'Rifiutata' }[s] || s)

        // ── Date utils ────────────────────────────────────────────────────────
        function today() {
            return new Date().toISOString().split('T')[0]
        }
        function addYear(d) {
            const dt = new Date(d)
            dt.setFullYear(dt.getFullYear() + 1)
            return dt.toISOString().split('T')[0]
        }
        function toInputDate(d) {
            if (!d) return today()
            return new Date(d).toISOString().split('T')[0]
        }

        return {
            dialog, loading, salvando, tab,
            nomeAzienda, abbonamento, tutteRichieste,
            form, formAbbRef, prorata,
            noteEvariazioni, evasionePendente, prorataRichieste,
            prezzoBaseCalcolato, prezzoExtraCalcolato, prezzoTotaleCalcolato,
            richiestePendentiLista, richiestePendenti,
            apri, chiudi,
            onCambioPiano, ricalcolaProrataAdmin,
            salvaAbbonamento, evadiRichiesta,
            formatDate, chipStatoLabel,
            labelTipoRichiesta, chipRichiestaColore, chipRichiestaLabel,
        }
    }
}
</script>

<style scoped>
.dialog-header { background: #f7f7f8; border-radius: 12px 12px 0 0; }
.riepilogo-grid { display: grid; grid-template-columns: 1fr auto; gap: 4px 16px; }
.richiesta-grid { display: grid; grid-template-columns: 140px 1fr; gap: 4px 12px; }
</style>